import { useCallback, useEffect, type ReactElement } from "react"
import { useRouter } from "next/router"

import { SignupLayout } from "@/app-shell/layouts/SignupLayout"
import {
  ClerkAuthFormSkeleton,
  ClerkSigninForm,
  ClerkSigninRedirectStatus,
} from "@/signup"
import { useAuth } from "@clerk/nextjs"

import { SupportContact } from "../components/SupportContact"
import { readSigninRedirectUrl } from "../utils/signinRedirect"
import {
  buildClerkInviteHref,
  readClerkInviteStatusQuery,
  readClerkTicketQuery,
} from "../utils/signupQuery"

const SigninRoute = () => {
  const router = useRouter()
  const { isLoaded, isSignedIn } = useAuth()

  const clerkTicket = readClerkTicketQuery(router.query.__clerk_ticket)
  const clerkInviteStatus = readClerkInviteStatusQuery(
    router.query.__clerk_status,
  )
  const isInviteRedirect = Boolean(
    clerkTicket &&
    (clerkInviteStatus === "sign_up" || clerkInviteStatus === "complete"),
  )
  const redirectUrl = clerkTicket
    ? "/home"
    : readSigninRedirectUrl(router.query.redirect_url)
  const signUpHref = clerkTicket
    ? buildClerkInviteHref("/signup", clerkTicket, "sign_up")
    : "/signup"

  useEffect(() => {
    if (router.isReady && isLoaded && isSignedIn) {
      void router.replace(redirectUrl)
    }
  }, [isLoaded, isSignedIn, redirectUrl, router, router.isReady])

  useEffect(() => {
    if (!isLoaded || isSignedIn || !router.isReady || !clerkTicket) {
      return
    }
    if (clerkInviteStatus === "sign_up") {
      void router.replace(
        buildClerkInviteHref("/signup", clerkTicket, "sign_up"),
      )
    } else if (clerkInviteStatus === "complete") {
      void router.replace("/home")
    }
  }, [clerkInviteStatus, clerkTicket, isLoaded, isSignedIn, router])

  const handleComplete = useCallback(() => {
    void router.replace(redirectUrl)
  }, [router, redirectUrl])

  if (!isLoaded || !router.isReady) {
    return <ClerkAuthFormSkeleton headingWidthClassName="w-36" />
  }

  if (isSignedIn) {
    return <ClerkSigninRedirectStatus />
  }

  if (isInviteRedirect) {
    return null
  }

  return (
    <div className="space-y-6">
      <ClerkSigninForm
        onComplete={handleComplete}
        signUpHref={signUpHref}
        redirectUrlComplete={redirectUrl}
        oauthCallbackUrl={`/sso-callback?return_to=${encodeURIComponent(redirectUrl)}`}
        invitationTicket={clerkTicket ?? undefined}
      />
    </div>
  )
}

SigninRoute.pageTitle = "Sign in"

SigninRoute.getLayout = (page: ReactElement) => (
  <SignupLayout>
    {page}
    <div className="mt-6">
      <SupportContact />
    </div>
  </SignupLayout>
)

export default SigninRoute
